import { applyAction, deserialize } from '$app/forms'
import type { Post } from '@coso/core/prisma/zod'
import { renderImage } from '@coso/image-editor/renderer'
import { type Media, type PostAttachment, type Role, type RoleType } from '@prisma/client'
import type { ActionResult } from '@sveltejs/kit'
import { clsx, type ClassValue } from 'clsx'
import { cubicOut } from 'svelte/easing'
import type { TransitionConfig } from 'svelte/transition'
import { twMerge } from 'tailwind-merge'
import { v4 } from 'uuid'
import type { ZodSchema } from 'zod'
import { userNotifications } from './stores/notifications'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithoutChild<T> = T extends { child?: any } ? Omit<T, 'child'> : T
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithoutChildren<T> = T extends { children?: any } ? Omit<T, 'children'> : T
export type WithoutChildrenOrChild<T> = WithoutChildren<WithoutChild<T>>
export type WithElementRef<T, U extends HTMLElement = HTMLElement> = T & { ref?: U | null }

export const flyAndScale = (node: Element, params = { y: -8, x: 0, start: 0.95, duration: 150 }): TransitionConfig => {
  const style = getComputedStyle(node)
  const transform = style.transform === 'none' ? '' : style.transform

  const scaleConversion = (valueA: number, scaleA: [number, number], scaleB: [number, number]) => {
    const [minA, maxA] = scaleA
    const [minB, maxB] = scaleB

    const percentage = (valueA - minA) / (maxA - minA)
    const valueB = percentage * (maxB - minB) + minB

    return valueB
  }

  const styleToString = (style: Record<string, number | string | undefined>): string => {
    return Object.keys(style).reduce((str, key) => {
      if (style[key] === undefined) return str
      return str + `${key}:${style[key]};`
    }, '')
  }

  return {
    duration: params.duration ?? 200,
    delay: 0,
    css: (t) => {
      const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0])
      const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0])
      const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1])

      return styleToString({
        transform: `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
        opacity: t,
      })
    },
    easing: cubicOut,
  }
}
export function userHasPermission(userRole: Role, needs: RoleType | undefined) {
  if (!needs) return true
  const rolesOrder = ['readonly', 'editor', 'admin', 'owner', 'account_manager']
  return rolesOrder.indexOf(userRole.type) >= rolesOrder.indexOf(needs)
}

export function subscriptionActive(organisation: any) {
  return organisation.billing?.status == 'active' ? true : organisation.paid
}

export function isChildRoute(parentUrl: string, urlToCheck: string) {
  urlToCheck = urlToCheck.replace(/\/\(app\)/, '')

  if (parentUrl == '') {
    return parentUrl === urlToCheck
  }

  // Escape special characters in the parent URL for the regex
  const escapedParentUrl = parentUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // Create a regex pattern to match the parent URL followed by a possible child route
  const pattern = new RegExp(`^${escapedParentUrl}(\\/|$)`)

  // Test the urlToCheck against the pattern
  return pattern.test(urlToCheck)
}

export function bytesToSizeString(bytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes == 0) return '0 Byte'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i]
}

/**
 * Deserializes a JSON string into an object
 */
export function objectFromEntries(entries: IterableIterator<[string, any]>): Record<string, any> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of entries) {
    const path = key.split(/[\[\]]/).filter(Boolean) // Split and remove empty strings
    let current = result

    for (let i = 0; i < path.length; i++) {
      const segment = path[i]
      const isLast = i === path.length - 1

      if (isLast) {
        if (value !== 'undefined') {
          if (Array.isArray(current)) {
            current.push(value)
          } else {
            current[segment] = value
          }
        }
      } else {
        // Check if next path segment is a numeric index (indicates an array)
        const nextSegment = path[i + 1]
        const isArray = !isNaN(parseInt(nextSegment, 10))

        if (isArray) {
          current[segment] = current[segment] || []
        } else {
          current[segment] = current[segment] || {}
        }
        //@ts-expect-error: We know that current[segment] is an object
        current = current[segment]
      }
    }
  }
  return result
}

export function createImage(description: string, key?: string) {
  const { promise, resolve } = Promise.withResolvers<Media>()

  const fd = new FormData()
  fd.append('description', description)

  if (key) {
    fd.append('key', key)
  }

  doAction('/?/createImage', fd, (media) => {
    resolve(media)
  })
  return promise
}

export function handleResult(
  result: ActionResult<Record<string, unknown>, Record<string, unknown>>,
  onSuccess?: (d: Record<string, unknown> | undefined) => void,
  onFailure?: (d: Record<string, unknown> | undefined) => void,
  onError?: (d: unknown) => void,
) {
  let action: string = ''
  switch (result.type) {
    case 'redirect':
    case 'success':
      // @ts-expect-error: We know that result.data is a Record<string, unknown>
      if (onSuccess) onSuccess(result.data)
      break
    case 'failure':
      if (result.status == 402) {
        action = 'payment'
      }
      userNotifications.append({
        title: result.data?.title || 'Failure: ' + result.status || '',
        message: result.data?.error || 'Something went wrong, please try again later.',
        level: 'error',
        type: 'popup',
        action: action!,
      })
      if (onFailure) onFailure(result.data)
      break
    case 'error':
      userNotifications.append({
        title: result.data?.title || 'Error: ' + result.status || '',
        message: result.data?.error || result.statusText || 'Something went wrong, please try again later.',
        level: 'error',
        type: 'popup',
        action: action!,
      })
      if (onError) onError(result.error)
      break
    default:
      break
  }
}

/**
 *  Validates a formData object against a zod schema. uses objectFromForm
 */
export function validateFormData<T extends ZodSchema>(schema: T, fd: FormData, safe: boolean = false) {
  const obj = objectFromEntries(fd.entries())

  if (safe) {
    return schema.safeParse(obj)
  }
  return schema.parse(obj)
}

export async function doAction(
  action: string,
  fd: FormData | object,
  onSuccess: (d: any) => void,
  onFailure?: (d: any) => void,
) {
  if (!(fd instanceof FormData)) {
    const entries = Object.entries(fd)
    fd = new FormData()
    for (const [key, value] of entries) {
      if (Array.isArray(value)) {
        for (let v of value) {
          fd.append(key, v)
        }
      } else if (value !== undefined) {
        fd.append(key, value)
      }
    }
  }
  await fetch(action, {
    method: 'POST',
    body: fd,
  })
    .then((r) => {
      if (r.status == 200) {
        return r.text()
      } else {
        throw new Error(r.statusText)
      }
    })
    .then((t) => deserialize(t))
    .then(async (result) => {
      handleResult(result, onSuccess, onFailure)
      await applyAction(result)
    })
    .catch((e) => {
      handleResult({ type: 'error', statusText: e.message }, onSuccess, onFailure, onFailure)
    })
}
async function completeMultipartUpload({ key, parts, uploadId }) {
  try {
    await fetch('/api/complete-multipart-upload.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key, parts, uploadId }),
    })
  } catch (error) {
    console.error(`Error in completeMultipartUpload on / route: ${error}`)
  }
}

export const uploadFile = async (
  file: File,
  key: string,
  bucket?: string,
  progress = (pct: number) => {
    console.debug(pct)
  },
) => {
  const { size, type } = file

  const { promise, resolve } = Promise.withResolvers<{
    bucket: string
    key: string
    readSignedUrl: string
    type: string
  }>()

  const response = await fetch(`/api/presigned-urls.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ key, size, bucket, contentType: type }),
  })

  const json = await response.json()

  const { multiPartUploadUrls, partCount, partSize, readSignedUrl, writeSignedUrl, uploadId, bucket: bkt } = json

  const reader = new FileReader()
  if (partCount === 1) {
    reader.onload = async () => {
      await fetch(writeSignedUrl, {
        method: 'PUT',
        body: reader.result,
        headers: {
          'Content-Type': type,
        },
      })
      progress(100)
      resolve({ bucket: bkt, key, readSignedUrl, type })
    }
  } else {
    const lastIndex = multiPartUploadUrls.length - 1

    let partsuploaded = 0

    progress(1)

    reader.onloadend = async () => {
      const uploadPromises = multiPartUploadUrls.map(async (uploadUrl: string, index: number) => {
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          body:
            index !== lastIndex
              ? reader.result.slice(index * partSize, (index + 1) * partSize)
              : reader.result.slice(index * partSize),
          headers: {
            'Content-Type': type,
            'Content-Length': index !== lastIndex ? partSize : size - index * partSize,
          },
        })

        partsuploaded++
        progress(Math.floor((partsuploaded / partCount) * 100))
        return res
      })
      const uploadResults = await Promise.all(uploadPromises)
      const parts = uploadResults.map((element, index) => ({
        ETag: element.headers.get('etag'),
        PartNumber: index + 1,
      }))

      await completeMultipartUpload({ parts, key, uploadId })
      resolve({ bucket: bkt, key, readSignedUrl, type })
    }
  }
  reader.readAsArrayBuffer(file)
  return await promise
}

export async function saveAttachment(
  brandId: string,
  postId: string,
  attachment: Post['attachments'][number],
  render: boolean = false,
) {
  if (attachment?.id) {
    if (render) {
      const dataurl = await renderImage(parseAttachment(attachment), { format: 'jpeg', multiplier: 1, quality: 80 })
      const file = dataURLtoFile(dataurl, attachment.id)
      const fileExtension = file.type.split('/')[1]
      const { bucket, key } = await uploadFile(
        file,
        `${brandId}/rendered/image/${attachment.id}.${fileExtension}`,
        undefined,
        () => {},
      )
      attachment.rendered = `${bucket}/${key}?now=${Date.now()}`
    } else {
      attachment.rendered = null
    }

    const fd = new FormData()
    fd.append('attachment', JSON.stringify(attachment))
    fd.append('postId', postId)

    return await new Promise<Array<PostAttachment>>((resolve) => {
      doAction('/scheduler?/saveAttachment', fd, ({ attachments }) => {
        resolve(attachments)
      })
    })
  }
}

export async function toDataURL(url: string): Promise<string> {
  return await fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        }),
    )
}

export function dataURLtoFile(dataurl: string, filename: string) {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

export function urlToBlob(url: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      // get the image blob
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = img.width
      canvas.height = img.height
      if (ctx) {
        ctx?.drawImage(img, 0, 0)
        ctx.canvas.toBlob(
          (blob) => {
            resolve(blob)
          },
          'image/png',
          1,
        )
      }
    }
    img.src = url
  })
}
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}
export function convertImageToBase64(url: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      // get the image blob
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = img.width
      canvas.height = img.height
      if (ctx) {
        ctx?.drawImage(img, 0, 0)
        resolve(ctx.canvas.toDataURL())
      }
    }

    img.src = url
  })
}
export async function fileToImage(file: File): Promise<HTMLImageElement> {
  return await loadImage(URL.createObjectURL(file))
}

export function fileToDataUrl(file: File): Promise<string> {
  const reader = new FileReader()
  const { promise, resolve } = Promise.withResolvers<string>()
  reader.onloadend = () => resolve(reader.result as string)
  reader.readAsDataURL(file)
  return promise
}

type UploadMediaOptions = {
  file: File
  brandId: string
  progress: (p: number) => void
  postId?: string
  folder?: string
}
export async function uploadMedia({ file, brandId, progress, postId, folder }: UploadMediaOptions) {
  const fd = new FormData()

  const filename = `${v4()}.${file.name.split('.').at(-1)}`

  const key = `${brandId}/media/${file.type.split('/')[0]}/${folder || 'uploaded'}/${filename}`

  const uploaded = await uploadFile(file, key, undefined, progress)

  console.log({ uploaded })

  fd.append(
    'media',
    JSON.stringify({
      bucket: uploaded.bucket,
      key: uploaded.key,
      description: '',
      name: file.name,
      source: folder || 'uploaded',
      type: file.type.split('/')[0],
      subtype: file.type.split('/')[1],
      size: file.size,
    }),
  )

  if (postId) {
    fd.append('postId', postId)
  }

  return await new Promise<Media>((resolve, reject) => {
    doAction('/library/uploaded?/upload', fd, (response) => {
      console.log({ response })
      resolve(response)
    }).catch(reject)
  })
}

export async function getAndUploadImages(url: string) {
  const CORS_enabled = await checkCORS(url)
  const params = new URLSearchParams()
  params.set('url', encodeURI(url))
  let result: string
  if (CORS_enabled) {
    result = await convertImageToBase64(url)
  } else {
    let image = await fetch(`/api/proxy?${params}`)
      .then(async (res) => {
        const x = new URL(url)
        const blob = await res.blob()
        return {
          blob: blob,
          filename: x.origin + x.pathname,
          contentType: res.headers.get('content-type'),
        }
      })
      .then(({ blob, contentType, filename }) => new File([blob], filename, { type: contentType || 'image/jpeg' }))

    result = URL.createObjectURL(image)
  }
  return result
}

export function getMediaUrl(src: string) {
  if (src?.startsWith('data:image/')) {
    return src
  }
  return '/media/' + src
}

export function debounce(func: (...args: any[]) => void, timeout: number) {
  let timer: NodeJS.Timeout
  return (...args: any[]) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func(...args)
    }, timeout)
  }
}

export function parseAttachment(attachment: Post['attachments'][number] | null) {
  if (!attachment) return
  if (attachment.loading) return

  const filters = attachment.edits?.filters || {}
  if (attachment.edits?.duotone) {
    filters.Duotone = attachment.edits.duotone
  }

  return {
    ...attachment.edits,
    src: attachment.mediaKey,
    rendered: attachment.rendered,
    media: attachment.media,
  }
}

export function checkCORS(url: string) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    xhr.open('HEAD', url, true)
    xhr.send()
    xhr.onreadystatechange = () => {
      xhr.getAllResponseHeaders()
      if (xhr.readyState == 4) {
        if (xhr.status == 200) {
          resolve(true)
        } else if (xhr.status == 0) {
          resolve(false)
        }
      }
    }
  })
}
